import axios from 'axios';
import { localApi } from './localApi.ts';
import { performSync } from './syncService.ts';
import { isBackendOnlineSync, isBackendOnline } from '../utils/backendStatus.ts';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';
import { isPWAInstalled } from '../utils/deviceUtils.ts';
import { getOfflineRestrictionMessage, isOfflineSupportedRequest } from '../utils/offlineCapabilities.ts';
import { enqueueSyncRequest, shouldEnqueueSyncRequest } from './syncQueue.ts';
import { getRecurringTransactionById } from './db/repositories/recurringTransactions.ts';
import { getAccountById } from './db/repositories/accounts.ts';
import { getCategoryById } from './db/repositories/categories.ts';
import { getTransactionById } from './db/repositories/transactions.ts';
import { getTaskById } from './db/repositories/tasks.ts';
import { getObjectiveById } from './db/repositories/okrObjectives.ts';
import { getKeyResultById } from './db/repositories/okrKeyResults.ts';
import { getIndicatorById } from './db/repositories/okrIndicators.ts';
import { getInvestmentPlanById } from './db/repositories/investmentPlans.ts';
import { getInvestmentScenarioById } from './db/repositories/investmentScenarios.ts';
import { getInvestmentLedgerById } from './db/repositories/investmentLedgers.ts';
import { getInvestmentRecordById } from './db/repositories/investmentRecords.ts';
import { getInvestmentAllocationById } from './db/repositories/investmentAllocations.ts';
import { getInvestmentGoalById } from './db/repositories/investmentGoals.ts';
import { SyncQueueEntityRef } from './db/repositories/syncQueue.ts';

const API_BASE_URL = getApiBaseUrl();

function isOnline(): boolean {
  // Verifica tanto internet quanto backend
  return navigator.onLine && isBackendOnlineSync();
}

function hasAuth(): boolean {
  return !!localStorage.getItem('token');
}

function shouldUseLocal(): boolean {
  // Modo offline só funciona quando instalado como PWA
  // Se não estiver instalado como PWA e estiver offline, não usar local
  if (!navigator.onLine && !isPWAInstalled()) {
    console.log('[hybridApi] Offline mas não é PWA instalada - não usar modo local');
    return false; // Forçar erro de rede em vez de usar local
  }
  
  // Use local if offline (PWA) OR if no auth (offline mode)
  return !isOnline() || !hasAuth();
}

function ensureOfflineSupported(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string) {
  if (!isOfflineSupportedRequest(method, url)) {
    throw new Error(getOfflineRestrictionMessage(url));
  }
}

function parseLocalIdFromUrl(url: string): number | null {
  const match = url.match(/\/(\d+)(?:\/|$)/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

function getEntityTypeFromUrl(url: string): SyncQueueEntityRef['type'] | null {
  if (url.includes('/api/v1/objectives') && url.includes('/key-results')) return 'key_results';
  if (url.includes('/api/v1/accounts')) return 'accounts';
  if (url.includes('/api/v1/categories')) return 'categories';
  if (url.includes('/api/v1/transactions')) return 'transactions';
  if (url.includes('/api/v1/tasks')) return 'tasks';
  if (url.includes('/api/v1/objectives')) return 'objectives';
  if (url.includes('/api/v1/key-results') && url.includes('/indicators')) return 'indicators';
  if (url.includes('/api/v1/key-results')) return 'key_results';
  if (url.includes('/api/v1/recurring-transactions')) return 'recurring_transactions';
  if (url.includes('/api/v1/investment-plans')) return 'investment_plans';
  if (url.includes('/api/v1/investment-tracking/ledgers')) return 'investment_ledgers';
  if (url.includes('/api/v1/investment-tracking/records')) return 'investment_records';
  if (url.includes('/api/v1/investment-advisory/scenarios')) return 'investment_scenarios';
  if (url.includes('/api/v1/investment-advisory/allocation')) return 'investment_allocations';
  if (url.includes('/api/v1/investment-advisory/goals')) return 'investment_goals';
  return null;
}

async function resolveEntityRef(url: string): Promise<SyncQueueEntityRef | undefined> {
  const type = getEntityTypeFromUrl(url);
  if (!type) return undefined;
  const localId = parseLocalIdFromUrl(url) ?? undefined;
  if (!localId) return { type };
  if (type === 'accounts') {
    const account = await getAccountById(localId);
    return { type, local_id: localId, server_id: account?.server_id };
  }
  if (type === 'categories') {
    const category = await getCategoryById(localId);
    return { type, local_id: localId, server_id: category?.server_id };
  }
  if (type === 'transactions') {
    const transaction = await getTransactionById(localId);
    return { type, local_id: localId, server_id: transaction?.server_id };
  }
  if (type === 'tasks') {
    const task = await getTaskById(localId);
    return { type, local_id: localId, server_id: task?.server_id };
  }
  if (type === 'objectives') {
    const objective = await getObjectiveById(localId);
    return { type, local_id: localId, server_id: objective?.server_id };
  }
  if (type === 'key_results') {
    const keyResult = await getKeyResultById(localId);
    return { type, local_id: localId, server_id: keyResult?.server_id };
  }
  if (type === 'indicators') {
    const indicator = await getIndicatorById(localId);
    return { type, local_id: localId, server_id: indicator?.server_id };
  }
  if (type === 'recurring_transactions') {
    const recurring = await getRecurringTransactionById(localId);
    return { type, local_id: localId, server_id: recurring?.server_id };
  }
  if (type === 'investment_plans') {
    const plan = await getInvestmentPlanById(localId);
    return { type, local_id: localId, server_id: plan?.server_id };
  }
  if (type === 'investment_scenarios') {
    const scenario = await getInvestmentScenarioById(localId);
    return { type, local_id: localId, server_id: scenario?.server_id };
  }
  if (type === 'investment_ledgers') {
    const ledger = await getInvestmentLedgerById(localId);
    return { type, local_id: localId, server_id: ledger?.server_id };
  }
  if (type === 'investment_records') {
    const record = await getInvestmentRecordById(localId);
    return { type, local_id: localId, server_id: record?.server_id };
  }
  if (type === 'investment_allocations') {
    const allocation = await getInvestmentAllocationById(localId);
    return { type, local_id: localId, server_id: allocation?.server_id };
  }
  if (type === 'investment_goals') {
    const goal = await getInvestmentGoalById(localId);
    return { type, local_id: localId, server_id: goal?.server_id };
  }
  return { type };
}

// Create axios instance for server
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const requestUrl: string = error.config?.url || '';
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
      const isAlreadyOnLogin =
        typeof window !== 'undefined' && window.location.pathname === '/login';

      // Se o 401 for do próprio login/registro, não redirecionar (senão o erro "pisca"
      // por causa do reload e o usuário não consegue ler a mensagem).
      if (!isAuthEndpoint && !isAlreadyOnLogin) {
        window.location.href = '/login';
      }
    }
    // If network error and we have local data, fallback to local
    if (!error.response && shouldUseLocal()) {
      console.log('Network error, falling back to local API');
      // Try to use local API for this request
      return localApi.get(error.config?.url || '', error.config);
    }
    return Promise.reject(error);
  }
);

// Hybrid API that switches between local and server
export const hybridApi = {
  get: async (url: string, config?: any) => {
    // Verificar se backend está realmente online
    const backendOnline = await isBackendOnline();
    
    if (shouldUseLocal() || !backendOnline) {
      console.log('[hybridApi] GET usando localApi (offline, sem token ou backend offline)');
      ensureOfflineSupported('GET', url);
      return localApi.get(url, config);
    }
    try {
      const response = await axiosInstance.get(url, config);
      console.log('[hybridApi] GET no servidor bem-sucedido');
      return response;
    } catch (error: any) {
      // Sempre fazer fallback para local em caso de erro de rede
      if (!error.response || error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        if (shouldUseLocal()) {
          console.log('[hybridApi] Server unavailable, using local');
          ensureOfflineSupported('GET', url);
          return localApi.get(url, config);
        }
        throw new Error('Backend indisponível. Conecte-se para continuar.');
      }
      // Para outros erros (401, 404, etc), ainda tenta local como fallback
      console.log('[hybridApi] Erro na requisição, tentando local como fallback');
      try {
        if (shouldUseLocal()) {
          ensureOfflineSupported('GET', url);
          return await localApi.get(url, config);
        }
        throw error;
      } catch (localError) {
        // Se local também falhar, lança o erro original
        throw error;
      }
    }
  },
  post: async (url: string, data?: any) => {
    console.log('[hybridApi] POST chamado para:', url);
    const backendOnline = await isBackendOnline();
    console.log('[hybridApi] Internet:', navigator.onLine);
    console.log('[hybridApi] Backend online:', backendOnline);
    console.log('[hybridApi] Tem token:', hasAuth());
    
    // Auth endpoints (login/register) should ALWAYS go to server if backend is online
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
    
    if (isAuthEndpoint) {
      // For auth endpoints, ALWAYS try server first
      // NEVER allow login when backend is offline - this prevents unauthorized access
      console.log('[hybridApi] Endpoint de autenticação - tentando servidor');
      
      if (!backendOnline) {
        console.error('[hybridApi] Backend offline - login não permitido');
        throw new Error('Backend não está acessível. Não é possível fazer login enquanto o servidor estiver offline.');
      }
      
      try {
        const response = await axiosInstance.post(url, data);
        console.log('[hybridApi] POST no servidor bem-sucedido');
        return response;
      } catch (error: any) {
        console.error('[hybridApi] Erro no POST de autenticação:', error);
        // Always throw error - don't allow fake logins
        throw error;
      }
    }
    
    // Se backend offline ou sem token, usar local
    if (shouldUseLocal() || !backendOnline) {
      console.log('[hybridApi] Usando localApi (offline, sem token ou backend offline)');
      ensureOfflineSupported('POST', url);
      const result = await localApi.post(url, data);
      if (shouldEnqueueSyncRequest('POST', url)) {
        const entityType = getEntityTypeFromUrl(url);
        if (!entityType) {
          return result;
        }
        const localId = result?.data?.id;
        await enqueueSyncRequest({
          method: 'POST',
          url,
          body: data,
          entity: {
            type: entityType,
            local_id: localId ?? undefined,
          },
        });
      }
      // Try to sync in background if backend comes back online
      if (backendOnline && hasAuth()) {
        performSync().catch(console.error);
      }
      return result;
    }
    try {
      console.log('[hybridApi] Tentando POST no servidor...');
      const response = await axiosInstance.post(url, data);
      console.log('[hybridApi] POST no servidor bem-sucedido');
      return response;
    } catch (error: any) {
      console.error('[hybridApi] Erro no POST:', error);
      if (!error.response) {
        if (shouldUseLocal()) {
          console.log('[hybridApi] Server unavailable, saving locally');
          ensureOfflineSupported('POST', url);
          const result = await localApi.post(url, data);
          if (shouldEnqueueSyncRequest('POST', url)) {
            const entityType = getEntityTypeFromUrl(url);
            if (!entityType) {
              return result;
            }
            const localId = result?.data?.id;
            await enqueueSyncRequest({
              method: 'POST',
              url,
              body: data,
              entity: {
                type: entityType,
                local_id: localId ?? undefined,
              },
            });
          }
          // Will sync when back online
          return result;
        }
        throw new Error('Backend indisponível. Conecte-se para continuar.');
      }
      throw error;
    }
  },
  put: async (url: string, data?: any) => {
    const backendOnline = await isBackendOnline();
    if (shouldUseLocal() || !backendOnline) {
      ensureOfflineSupported('PUT', url);
      const entityRef = await resolveEntityRef(url);
      const result = await localApi.put(url, data);
      if (shouldEnqueueSyncRequest('PUT', url)) {
        await enqueueSyncRequest({
          method: 'PUT',
          url,
          body: data,
          entity: entityRef,
        });
      }
      if (backendOnline && hasAuth()) {
        performSync().catch(console.error);
      }
      return result;
    }
    try {
      return await axiosInstance.put(url, data);
    } catch (error: any) {
      if (!error.response) {
        if (shouldUseLocal()) {
          console.log('[hybridApi] Server unavailable, saving locally');
          ensureOfflineSupported('PUT', url);
          const entityRef = await resolveEntityRef(url);
          const result = await localApi.put(url, data);
          if (shouldEnqueueSyncRequest('PUT', url)) {
            await enqueueSyncRequest({
              method: 'PUT',
              url,
              body: data,
              entity: entityRef,
            });
          }
          return result;
        }
        throw new Error('Backend indisponível. Conecte-se para continuar.');
      }
      throw error;
    }
  },
  patch: async (url: string, data?: any) => {
    const backendOnline = await isBackendOnline();
    if (shouldUseLocal() || !backendOnline) {
      ensureOfflineSupported('PATCH', url);
      const entityRef = await resolveEntityRef(url);
      const result = await localApi.patch(url, data);
      if (shouldEnqueueSyncRequest('PATCH', url)) {
        await enqueueSyncRequest({
          method: 'PATCH',
          url,
          body: data,
          entity: entityRef,
        });
      }
      if (backendOnline && hasAuth()) {
        performSync().catch(console.error);
      }
      return result;
    }
    try {
      return await axiosInstance.patch(url, data);
    } catch (error: any) {
      if (!error.response) {
        if (shouldUseLocal()) {
          console.log('[hybridApi] Server unavailable, saving locally');
          ensureOfflineSupported('PATCH', url);
          const entityRef = await resolveEntityRef(url);
          const result = await localApi.patch(url, data);
          if (shouldEnqueueSyncRequest('PATCH', url)) {
            await enqueueSyncRequest({
              method: 'PATCH',
              url,
              body: data,
              entity: entityRef,
            });
          }
          return result;
        }
        throw new Error('Backend indisponível. Conecte-se para continuar.');
      }
      throw error;
    }
  },
  delete: async (url: string) => {
    const backendOnline = await isBackendOnline();
    if (shouldUseLocal() || !backendOnline) {
      ensureOfflineSupported('DELETE', url);
      const entityRef = await resolveEntityRef(url);
      const result = await localApi.delete(url);
      if (shouldEnqueueSyncRequest('DELETE', url)) {
        await enqueueSyncRequest({
          method: 'DELETE',
          url,
          entity: entityRef,
        });
      }
      if (backendOnline && hasAuth()) {
        performSync().catch(console.error);
      }
      return result;
    }
    try {
      return await axiosInstance.delete(url);
    } catch (error: any) {
      if (!error.response) {
        if (shouldUseLocal()) {
          console.log('[hybridApi] Server unavailable, deleting locally');
          ensureOfflineSupported('DELETE', url);
          const entityRef = await resolveEntityRef(url);
          const result = await localApi.delete(url);
          if (shouldEnqueueSyncRequest('DELETE', url)) {
            await enqueueSyncRequest({
              method: 'DELETE',
              url,
              entity: entityRef,
            });
          }
          return result;
        }
        throw new Error('Backend indisponível. Conecte-se para continuar.');
      }
      throw error;
    }
  }
};

